import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, Trash2, Loader2, Save, Plus, X, ArrowUp, ArrowDown } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { logoSrc, useBranding } from "@/context/BrandingContext";
import { PALETTES, PRESETS } from "@/lib/brandingOptions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";

export default function BrandingSettingsPage() {
  const { branding, refresh } = useBranding();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    if (branding && !form) {
      setForm({
        product_name: branding.product_name,
        primary_color: branding.primary_color,
        accent_color: branding.accent_color,
        preset: branding.preset,
        login_announcements: {
          enabled: branding.login_announcements?.enabled ?? true,
          lines: [...(branding.login_announcements?.lines || [])],
        },
      });
    }
  }, [branding]); // eslint-disable-line

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/tenant/branding", form);
      await refresh();
      toast.success("Branding saved — applied everywhere including PDF exports");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const uploadLogo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      await api.post("/tenant/branding/logo", fd);
      await refresh();
      toast.success("Logo uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const removeLogo = async () => {
    await api.delete("/tenant/branding/logo");
    await refresh();
    toast.success("Logo removed — monogram fallback active");
  };

  if (!form) return <Skeleton className="h-96 w-full rounded-none" />;

  const ann = form.login_announcements;
  const setAnn = (updater) => setForm((f) => ({
    ...f, login_announcements: updater(f.login_announcements) }));
  const setLine = (i, value) => setAnn((a) => ({
    ...a, lines: a.lines.map((l, li) => (li === i ? value : l)) }));
  const moveLine = (i, dir) => setAnn((a) => {
    const lines = [...a.lines];
    const j = i + dir;
    if (j < 0 || j >= lines.length) return a;
    [lines[i], lines[j]] = [lines[j], lines[i]];
    return { ...a, lines };
  });

  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="branding-settings-page">
      <div className="bg-white border border-slate-200 p-6 space-y-5">
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-slate-500">Product name</Label>
          <Input data-testid="branding-product-name-input" value={form.product_name}
            onChange={set("product_name")} className="rounded-none mt-1" />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-slate-500">Logo</Label>
          <div className="flex items-center gap-3 mt-1.5">
            {branding.has_logo
              ? <img src={`${logoSrc()}&v=${Date.now()}`} alt="logo" className="h-10 w-auto border border-slate-200 p-1" data-testid="branding-current-logo" />
              : <span className="text-[11px] text-slate-400">No logo — monogram fallback in use</span>}
            <label className="flex items-center gap-2 border border-slate-300 px-3 py-2 text-[11px] cursor-pointer hover:bg-slate-50">
              <Upload className="h-3.5 w-3.5" /> Upload
              <input data-testid="branding-logo-input" type="file" accept="image/png,image/jpeg,image/webp" onChange={uploadLogo} className="hidden" />
            </label>
            {branding.has_logo && (
              <Button variant="outline" data-testid="branding-logo-remove-btn" onClick={removeLogo}
                className="rounded-none h-8 text-[11px] border-red-300 text-red-700 hover:bg-red-50">
                <Trash2 className="h-3.5 w-3.5" /> Remove
              </Button>
            )}
          </div>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-slate-500">Palette presets</Label>
          <div className="grid grid-cols-4 gap-2 mt-1.5">
            {PALETTES.map((p) => (
              <button key={p.name} data-testid={`branding-palette-${p.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                onClick={() => setForm((f) => ({ ...f, primary_color: p.primary, accent_color: p.accent }))}
                className={`border p-2 text-left hover:border-slate-400 ${form.primary_color === p.primary && form.accent_color === p.accent ? "border-2" : "border-slate-200"}`}
                style={form.primary_color === p.primary && form.accent_color === p.accent ? { borderColor: p.accent } : {}}>
                <div className="flex gap-1">
                  <div className="h-4 w-4" style={{ background: p.primary }} />
                  <div className="h-4 w-4" style={{ background: p.accent }} />
                </div>
                <p className="text-[9px] text-slate-500 mt-1 truncate">{p.name}</p>
              </button>
            ))}
          </div>
          <div className="flex items-center gap-4 mt-3">
            <label className="flex items-center gap-2 text-[11px] text-slate-500">Primary
              <input data-testid="branding-primary-color" type="color" value={form.primary_color} onChange={set("primary_color")} className="h-7 w-10 border border-slate-300 cursor-pointer" />
            </label>
            <label className="flex items-center gap-2 text-[11px] text-slate-500">Accent
              <input data-testid="branding-accent-color" type="color" value={form.accent_color} onChange={set("accent_color")} className="h-7 w-10 border border-slate-300 cursor-pointer" />
            </label>
          </div>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-slate-500">Design preset</Label>
          <div className="grid grid-cols-2 gap-2 mt-1.5">
            {PRESETS.map((p) => (
              <button key={p.id} data-testid={`branding-preset-${p.id}`}
                onClick={() => setForm((f) => ({ ...f, preset: p.id }))}
                className={`border p-3 text-left hover:border-slate-400 ${form.preset === p.id ? "border-2 border-slate-700" : "border-slate-200"}`}>
                <p className="text-[12px] font-bold text-slate-800">{p.name}</p>
                <p className="text-[10px] text-slate-500 mt-0.5">{p.blurb}</p>
              </button>
            ))}
          </div>
        </div>
        <div>
          <div className="flex items-center justify-between">
            <Label className="text-[11px] uppercase tracking-wider text-slate-500">Login announcements</Label>
            <Switch checked={ann.enabled} data-testid="announcements-enabled-switch"
              onCheckedChange={(v) => setAnn((a) => ({ ...a, enabled: v }))} />
          </div>
          <p className="text-[11px] text-slate-400 mt-1">
            Rotating one-line value props under the login card — up to 5, shown in order.
          </p>
          <div className="mt-2 space-y-1.5" data-testid="announcements-editor">
            {ann.lines.map((line, i) => (
              <div key={i} className="flex items-center gap-1.5">
                <Input value={line} maxLength={140} data-testid={`announcement-line-${i}`}
                  onChange={(e) => setLine(i, e.target.value)}
                  className="rounded-none h-8 text-[12px]" />
                <button onClick={() => moveLine(i, -1)} disabled={i === 0} title="Move up"
                  data-testid={`announcement-up-${i}`}
                  className="border border-slate-200 p-1.5 hover:bg-slate-50 disabled:opacity-30">
                  <ArrowUp className="h-3 w-3" />
                </button>
                <button onClick={() => moveLine(i, 1)} disabled={i === ann.lines.length - 1} title="Move down"
                  data-testid={`announcement-down-${i}`}
                  className="border border-slate-200 p-1.5 hover:bg-slate-50 disabled:opacity-30">
                  <ArrowDown className="h-3 w-3" />
                </button>
                <button title="Remove" data-testid={`announcement-remove-${i}`}
                  onClick={() => setAnn((a) => ({ ...a, lines: a.lines.filter((_, li) => li !== i) }))}
                  className="border border-red-200 text-red-600 p-1.5 hover:bg-red-50">
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
            {ann.lines.length < 5 && (
              <button data-testid="announcement-add-btn"
                onClick={() => setAnn((a) => ({ ...a, lines: [...a.lines, ""] }))}
                className="flex items-center gap-1.5 border border-slate-300 px-3 py-1.5 text-[11px] hover:bg-slate-50">
                <Plus className="h-3 w-3" /> Add line
              </button>
            )}
          </div>
        </div>
        <Button data-testid="branding-save-btn" disabled={saving} onClick={save}
          className="rounded-none text-white" style={{ background: "var(--brand-primary)" }}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save branding
        </Button>
      </div>

      <div className="bg-white border border-slate-200 p-6">
        <p className="text-[10px] uppercase tracking-[0.18em] text-slate-400 mb-3">Live preview</p>
        <div className="border border-slate-200 flex" data-testid="branding-live-preview">
          <div className="w-40 p-4" style={{ background: form.preset === "minimal" ? "#fff" : form.primary_color, borderRight: form.preset === "minimal" ? "1px solid #e2e8f0" : "none" }}>
            <div className="flex items-center gap-2">
              <div className="h-6 w-6 flex items-center justify-center text-[12px] font-bold text-white relative" style={{ background: form.primary_color, border: "1px solid rgba(255,255,255,.2)" }}>
                {(form.product_name || "A")[0].toUpperCase()}
                <div className="absolute bottom-0 left-0 right-0 h-[2px]" style={{ background: form.accent_color }} />
              </div>
              <span className="text-[10px] font-bold truncate" style={{ color: form.preset === "minimal" ? "#0f172a" : "#fff" }}>
                {(form.product_name || "").toUpperCase()}
              </span>
            </div>
            <div className="mt-4 space-y-2">
              {["Dashboard", "Employees", "Payroll"].map((l, i) => (
                <div key={l} className="text-[10px] px-2 py-1.5" style={{
                  color: i === 0 ? (form.preset === "minimal" ? "#0f172a" : "#fff") : (form.preset === "minimal" ? "#64748b" : "rgba(255,255,255,.55)"),
                  borderLeft: i === 0 ? `2px solid ${form.accent_color}` : "2px solid transparent",
                  background: i === 0 ? (form.preset === "minimal" ? "rgba(15,23,42,.04)" : "rgba(255,255,255,.06)") : "transparent",
                }}>{l}</div>
              ))}
            </div>
          </div>
          <div className="flex-1 p-4 bg-[#F8FAFC]">
            <p className="text-[13px] font-bold" style={{ color: form.primary_color, fontFamily: form.preset === "classic" ? "Georgia, serif" : "inherit" }}>Dashboard</p>
            <div className="grid grid-cols-2 gap-2 mt-3">
              {[["Headcount", "128"], ["Compliance", "96%"]].map(([k, v]) => (
                <div key={k} className="bg-white border border-slate-200 p-3" style={{ borderRadius: form.preset === "modern" ? 8 : 0 }}>
                  <p className="text-[9px] uppercase tracking-wider text-slate-400">{k}</p>
                  <p className="text-[16px] font-bold font-mono" style={{ color: form.primary_color }}>{v}</p>
                </div>
              ))}
            </div>
            <button className="mt-3 text-[10px] font-semibold text-white px-3 py-1.5" style={{ background: form.accent_color, borderRadius: form.preset === "modern" ? 6 : 0 }}>
              Primary action
            </button>
          </div>
        </div>
        <p className="text-[11px] text-slate-400 mt-4 leading-relaxed">
          Branding applies to the login page, sidebar, browser tab, favicon and every PDF export
          (payslips, board packs, compliance reports, committee briefings).
        </p>
      </div>
    </div>
  );
}
